import React, { useState, useEffect } from 'react';
import Icon from '../../../AppIcon';
import Image from '../../../AppImage';

const TestimonialsSection = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
  {
    id: 1,
    name: 'Sarah Chen',
    role: 'VP Innovation',
    company: 'TechCorp Global',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_129b1ff05-1762273604073.png",
    avatarAlt: 'Professional Asian woman with shoulder-length black hair in navy blazer smiling at camera',
    content: `ScoutBridge transformed our startup discovery process. We've identified and partnered with 12 breakthrough startups in just 6 months, generating $2.3M in new revenue streams. The AI matching is incredibly accurate.`,
    rating: 5,
    metrics: {
      partnerships: 12,
      revenue: '$2.3M', timeframe: '6 months'
    },
    category: 'Corporate'
  },
  {
    id: 2,
    name: 'Marcus Rodriguez', role: 'Founder & CEO', company: 'GreenTech Innovations', avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_146fb5593-1762274292741.png", avatarAlt: 'Hispanic male entrepreneur with short dark hair and beard in white shirt smiling confidently',
    content: `Through ScoutBridge, we secured partnerships with 3 Fortune 500 companies and raised $5M in Series A funding. The verification process gave corporates confidence in our capabilities from day one.`,
    rating: 5,
    metrics: {
      partnerships: 3,
      funding: '$5M', timeframe: 'Series A'
    },
    category: 'Startup'
  },
  {
    id: 3,
    name: 'Dr. Elena Kowalski', role: 'Innovation Hub Director', company: 'Berlin Innovation Network', avatar: "https://images.unsplash.com/photo-1734033388488-6260d1c9bf40", avatarAlt: 'Professional European woman with blonde hair in glasses and dark blazer in modern office setting',
    content: `Our referral network generated €180K in commissions last year through ScoutBridge. The platform's analytics help us identify the most promising startups and optimize our matching success rate.`,
    rating: 5,
    metrics: {
      commissions: '€180K',
      matches: 47,
      successRate: '89%'
    },
    category: 'Intermediary'
  },
  {
    id: 4,
    name: 'James Thompson',
    role: 'Field Operations Manager',
    company: 'Innovation Scouts Ltd',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd689e9f-1762248928883.png",
    avatarAlt: 'Professional Caucasian man with brown hair in blue shirt smiling in outdoor business environment',
    content: `The mobile verification tools are game-changing. I can validate startup credentials in real-time, even in remote locations. Our data quality scores improved by 40% since using ScoutBridge.`,
    rating: 5,
    metrics: {
      dataQuality: '+40%',
      verifications: 234,
      efficiency: '+60%'
    },
    category: 'Field Operations'
  }];


  const successStories = [
  {
    title: 'AI Healthcare Breakthrough',
    description: 'MedTech startup partnered with pharmaceutical giant for $50M joint venture',
    image: "https://images.unsplash.com/photo-1666214280577-5f90bc36be92",
    imageAlt: 'Medical professionals examining AI diagnostic equipment in modern healthcare facility',
    value: '$50M',
    industry: 'Healthcare',
    timeline: '8 weeks'
  },
  {
    title: 'Sustainable Energy Alliance',
    description: 'CleanTech startup secured corporate partnership for global renewable deployment',
    image: "https://images.unsplash.com/photo-1650501889487-0b782e1afe26",
    imageAlt: 'Solar panel installation with wind turbines in background under blue sky with white clouds',
    value: '$125M',
    industry: 'Energy',
    timeline: '12 weeks'
  },
  {
    title: 'FinTech Innovation Hub',
    description: 'Banking consortium partnered with 5 FinTech startups for digital transformation',
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f",
    imageAlt: 'Modern financial technology dashboard with digital banking interface and data analytics',
    value: '$30M',
    industry: 'Financial Services',
    timeline: '6 weeks'
  }];


  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) =>
    <Icon
      key={index}
      name="Star"
      size={16}
      className={index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'} />

    );
  };

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="MessageSquare" size={16} />
            <span>Success Stories</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Trusted by Innovation Leaders
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Discover how organizations across the globe are transforming their innovation strategies with ScoutBridge's intelligent matching ecosystem.
          </p>
        </div>

        {/* Main Testimonial */}
        <div className="mb-16">
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute top-0 right-0 w-64 h-64 opacity-5">
              <Icon name="Quote" size={256} className="text-primary" />
            </div>

            <div className="relative grid lg:grid-cols-3 gap-8 items-center">
              {/* Testimonial Content */}
              <div className="lg:col-span-2 space-y-6">
                <div className="flex items-center space-x-1 mb-4">
                  {renderStars(testimonials?.[activeTestimonial]?.rating)}
                </div>

                <blockquote className="text-xl lg:text-2xl text-slate-700 leading-relaxed font-medium">
                  "{testimonials?.[activeTestimonial]?.content}"
                </blockquote>

                {/* Metrics */}
                <div className="flex flex-wrap gap-6">
                  {Object.entries(testimonials?.[activeTestimonial]?.metrics)?.map(([key, value]) =>
                  <div key={key} className="text-center">
                      <div className="text-2xl font-bold text-primary">{value}</div>
                      <div className="text-sm text-slate-600 capitalize">{key?.replace(/([A-Z])/g, ' $1')}</div>
                    </div>
                  )}
                </div>

                {/* Author Info */}
                <div className="flex items-center space-x-4 pt-4 border-t border-slate-200">
                  <div className="w-16 h-16 rounded-full overflow-hidden">
                    <Image
                      src={testimonials?.[activeTestimonial]?.avatar}
                      alt={testimonials?.[activeTestimonial]?.avatarAlt}
                      className="w-full h-full object-cover" />

                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900">
                      {testimonials?.[activeTestimonial]?.name}
                    </h4>
                    <p className="text-slate-600">
                      {testimonials?.[activeTestimonial]?.role}
                    </p>
                    <p className="text-sm text-slate-500">
                      {testimonials?.[activeTestimonial]?.company}
                    </p>
                  </div>
                </div>
              </div>

              {/* Category Badge */}
              <div className="lg:col-span-1 flex justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Icon
                      name={
                      testimonials?.[activeTestimonial]?.category === 'Corporate' ? 'Building2' :
                      testimonials?.[activeTestimonial]?.category === 'Startup' ? 'Rocket' :
                      testimonials?.[activeTestimonial]?.category === 'Intermediary' ? 'Users' : 'MapPin'
                      }
                      size={32}
                      color="white" />

                  </div>
                  <div className="text-lg font-semibold text-slate-900">
                    {testimonials?.[activeTestimonial]?.category}
                  </div>
                  <div className="text-sm text-slate-600">Success Story</div>
                </div>
              </div>
            </div>

            {/* Navigation Dots */}
            <div className="flex justify-center space-x-2 mt-8">
              {testimonials?.map((_, index) =>
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                index === activeTestimonial ? 'bg-primary' : 'bg-slate-300'}`
                } />

              )}
            </div>
          </div>
        </div>

        {/* Success Stories Grid */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-slate-900 text-center mb-8">
            Recent Partnership Successes
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            {successStories?.map((story, index) =>
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">

                <div className="relative h-48 overflow-hidden">
                  <Image
                  src={story?.image}
                  alt={story?.imageAlt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />

                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1">
                    <span className="text-sm font-medium text-slate-900">{story?.industry}</span>
                  </div>
                  <div className="absolute top-4 right-4 bg-emerald-500 text-white rounded-lg px-3 py-1">
                    <span className="text-sm font-bold">{story?.value}</span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">
                    {story?.title}
                  </h4>
                  <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                    {story?.description}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>Timeline: {story?.timeline}</span>
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={12} />
                      <span>Completed</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="bg-gradient-to-r from-slate-900 to-blue-900 rounded-2xl p-8 text-white">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">Trusted by Industry Leaders</h3>
            <p className="text-white/90">Join thousands of organizations driving innovation through ScoutBridge</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
            { metric: '29,200+', label: 'Active Users' },
            { metric: '85+', label: 'Countries' },
            { metric: '94.2%', label: 'Success Rate' },
            { metric: '$2.8B+', label: 'Value Created' }]?.
            map((stat, index) =>
            <div key={index} className="text-center">
                <div className="text-3xl font-bold text-white mb-1">{stat?.metric}</div>
                <div className="text-white/70 text-sm">{stat?.label}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;